import { Stepper, Step, StepLabel } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import ChoseType from './newTestDialog/ChoseType';
import { QUESTIONS_OBJ, TEST_OBJ } from '../utils/constants';
import WritePaper from './newTestDialog/WritePaper';
import UploadPaper from './newTestDialog/UploadPaper';
import ChooseOldPaper from './newTestDialog/ChooseOldPaper';
import TestSetting from './newTestDialog/TestSetting';
import TestPreview from './newTestDialog/TestPreview';
import Modal from './common/Modal';

const STEP_NAME = ['Choose Type', 'Test Paper', 'Settings', 'Finish'];

export default function AddNewTestModal({ isOpen, isClose, editObj }) {
  const [activeStep, setActiveStep] = useState(0);
  const [testObj, setTestObj] = useState(TEST_OBJ);
  const [testSubmitting, setTestSubmitting] = useState(false);
  const [writeQuestionArray, setWriteQuesArray] = useState(QUESTIONS_OBJ);
  const choseTypeRef = useRef();
  const writePaperRef = useRef();
  const uploadPaperRef = useRef();
  const settingRef = useRef();
  const previewRef = useRef();

  useEffect(() => {
    if (editObj && isOpen) {
      const newTestObj = {
        testName: editObj.name,
        testType: editObj.type.toUpperCase(),
        description: editObj?.description ?? '',
        marking: {
          correct: editObj.correctMark,
          incorrect: editObj?.incorrectMark ?? 0,
          total: editObj.totalMark
        },
        duration: editObj.duration,
        startDate: editObj.startDate,
        startTime: editObj.startTime,
        isPracticeTest: editObj.isPractice,
        isPublishResult: editObj.isPublishResult,
        isRandomize: editObj.isRandomize
      };
      if (editObj.type === 'type') {
        const updatedQuesArray = editObj.questions.map((qu) => ({
          _id: qu._id,
          name: qu.name,
          isError: false,
          isAnsNotSelected: false,
          // TODO: when image is added then we have to make req to get storage url
          // image: '',
          options: qu.options.map((op) => ({
            value: op.value,
            isAnswer: op.isAnswer,
            isError: false,
            _id: op._id
          }))
        }));
        newTestObj.questions = updatedQuesArray;
        setWriteQuesArray(updatedQuesArray);
      }
      if (editObj.type === 'upload') {
        newTestObj.numOfQuestions = editObj.totalQuestions;
        newTestObj.numOfOptions = editObj.optionsPerQuestion;
        newTestObj.testFile = { name: editObj?.typeURL ?? '' };
        newTestObj.questions = editObj?.answerSheet ?? [];
      }
      setTestObj(newTestObj);
    }
  }, [editObj, isOpen]);

  const handleChooseTypeSend = (name, type) => {
    testObj.testName = name;
    testObj.testType = type;
    setTestObj(testObj);
    handleNext();
  };

  const handleWriteTestSend = (questionArray) => {
    testObj.questions = questionArray;
    setWriteQuesArray(questionArray);
    setTestObj(testObj);
    handleNext();
  };

  const handleSettingSend = (finalTestObj) => {
    const {
      testName,
      description,
      marking,
      duration,
      startDate,
      startTime,
      isPracticeTest,
      isPublishResult,
      isRandomize
    } = finalTestObj;
    testObj.testName = testName;
    testObj.description = description;
    testObj.marking.correct = parseInt(marking.correct, 10);
    testObj.marking.incorrect = parseInt(marking.incorrect, 10);
    testObj.marking.total = parseInt(marking.total, 10);
    // eslint-disable-next-line
    testObj.duration = parseInt(duration);
    testObj.startDate = startDate;
    testObj.startTime = startTime;
    testObj.isPracticeTest = isPracticeTest;
    testObj.isPublishResult = isPublishResult;
    testObj.isRandomize = isRandomize;
    setTestObj(testObj);
    handleNext();
  };

  const handleUploadTest = (uploadTestData) => {
    const isNumOptionsKey = Object.prototype.hasOwnProperty.call(uploadTestData, 'numOfQuestions');
    testObj.testFile = uploadTestData.testFile;
    if (isNumOptionsKey) {
      testObj.numOfQuestions = uploadTestData.numOfQuestions;
      testObj.numOfOptions = uploadTestData.numOfOptions;
      testObj.questions = uploadTestData.questions;
    }
    setTestObj(testObj);
    handleNext();
    // testObj.tes
  };

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleClose = () => {
    isClose();
    setActiveStep(0);
    setWriteQuesArray([
      {
        _id: nanoid(),
        name: '',
        isError: false,
        isAnsNotSelected: true,
        options: [
          {
            _id: nanoid(),
            value: '',
            isAnswer: false,
            isError: false
          },
          {
            _id: nanoid(),
            value: '',
            isAnswer: false,
            isError: false
          }
        ]
      }
    ]);
    setTestObj({
      id: nanoid(),
      testName: '',
      testType: 'WRITE',
      description: '',
      marking: {
        correct: 0,
        incorrect: 0,
        total: 0
      },
      duration: 0,
      startDate: new Date(),
      startTime: new Date(),
      createdAt: new Date(),
      isPracticeTest: false,
      isPublishResult: false,
      isRandomize: false
    });
  };

  const getCancelBtnFunction = () => {
    if (activeStep === 0) return handleClose();
    return handleBack();
  };

  const getActionBtnFunction = () => {
    if (activeStep === 0) {
      choseTypeRef.current.handleChoseType();
      // handleChooseTypeSend();
    }
    if (activeStep === 1 && testObj.testType === 'TYPE') {
      writePaperRef.current.handlePaperSubmit();
    }
    if (activeStep === 1 && testObj.testType === 'UPLOAD') {
      uploadPaperRef.current.handleUploadPaperSubmit();
    }
    if (activeStep === 2) {
      settingRef.current.handleSettingSubmit();
    }
    if (activeStep === 3) {
      previewRef.current.handleSubmitTest();
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => handleClose()}
      title="Add New Test"
      cancelText={activeStep === 0 ? 'Cancel' : 'Back'}
      onCancel={() => getCancelBtnFunction()}
      onAction={getActionBtnFunction}
      actionText={activeStep === 3 ? 'Finish' : 'Next'}
      isBtnLoading={testSubmitting}
      isCancelDisabled={testSubmitting}
    >
      <Stepper activeStep={activeStep} alternativeLabel>
        {STEP_NAME.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {activeStep === 0 && (
        <ChoseType
          handleNext={handleChooseTypeSend}
          ref={choseTypeRef}
          isCancel={handleClose}
          testName={testObj.testName}
          testType={testObj.testType}
        />
      )}
      {activeStep === 1 && testObj.testType === 'TYPE' && (
        <WritePaper
          handleBack={handleBack}
          ref={writePaperRef}
          questions={writeQuestionArray}
          updateArray={(d) => setWriteQuesArray(d)}
          handleNext={handleWriteTestSend}
        />
      )}
      {activeStep === 1 && testObj.testType === 'UPLOAD' && (
        <UploadPaper
          handleBack={handleBack}
          handleNext={handleUploadTest}
          ref={uploadPaperRef}
          testObj={testObj}
        />
      )}
      {activeStep === 1 && testObj.testType === 'OLD_PAPER' && (
        // TODO: have to add additional call for get all previous data and select any old paper
        <ChooseOldPaper handleBack={handleBack} />
      )}
      {activeStep === 2 && (
        <TestSetting
          testObj={testObj}
          ref={settingRef}
          handleBack={handleBack}
          handleNext={handleSettingSend}
        />
      )}
      {activeStep === 3 && (
        <TestPreview
          testObj={testObj}
          editObj={editObj}
          ref={previewRef}
          handleBack={handleBack}
          onClose={handleClose}
          setTestSubmitting={setTestSubmitting}
        />
      )}
    </Modal>
  );
}

AddNewTestModal.propTypes = {
  isOpen: PropTypes.bool,
  isClose: PropTypes.func,
  editObj: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    answerSheet: PropTypes.array,
    description: PropTypes.string,
    correctMark: PropTypes.number,
    incorrectMark: PropTypes.number,
    totalMark: PropTypes.number,
    duration: PropTypes.number,
    startDate: PropTypes.string,
    startTime: PropTypes.string,
    isPractice: PropTypes.bool,
    isPublishResult: PropTypes.bool,
    isRandomize: PropTypes.bool,
    questions: PropTypes.arrayOf(PropTypes.shape({})),
    totalQuestions: PropTypes.number,
    optionsPerQuestion: PropTypes.number,
    typeURL: PropTypes.string
  })
};
